<template>
  <div class="modal-overlay top z1200 gp-overlay" @click.self="$emit('close')">
    <div class="gp-modal" role="dialog" aria-modal="true" :aria-label="'Choose ' + slot">
      <div class="gp-head">
        <h3>Choose {{ slot }}</h3>
        <label v-if="unlockable" class="gp-showall" title="Show every item, ignoring class restrictions">
          <input type="checkbox" :checked="showAll" @change="$emit('toggle-showall')"> unlock all
        </label>
        <button class="gp-close" @click="$emit('close')" aria-label="Close">✕</button>
      </div>
      <input ref="search" class="gp-search" v-model="query" placeholder="Search items…">
      <div class="gp-body">
        <div class="gp-tiles">
          <button class="gp-tile gp-empty" :class="{ on: !value }" @click="pick('')">
            <span class="gp-x">✕</span><span class="gp-name">— empty —</span>
          </button>
        </div>
        <div v-for="(g, gi) in filteredGroups" :key="g.label || gi" class="gp-group">
          <div v-if="g.label" class="gp-grouplabel">{{ g.label }}</div>
          <div class="gp-tiles">
            <button v-for="it in g.items" :key="it" class="gp-tile" :class="{ on: it === value }"
                    @click="pick(it)" :title="infoFor(it)">
              <img class="gp-icon" :src="iconUrl(it)" :alt="it" @error="$event.target.style.visibility = 'hidden'">
              <span class="gp-meta">
                <span class="gp-name">{{ it }}</span>
                <span class="gp-stats">{{ metaFor(it) }}</span>
              </span>
            </button>
          </div>
        </div>
        <div v-if="!filteredGroups.length" class="gp-none">No matching items.</div>
      </div>
    </div>
  </div>
</template>

<script>
// Custom gear picker (replaces the native <select>): a searchable grid of item
// tiles with icon + class-legal grouping + stat summary + the item's info on hover.
// Icons reuse the inline card's source (icons/<name-no-spaces>.gif).
import modalA11y from '../mixins/modalA11y.js';
import { itemStatSummary } from '../lib/classGear.js';

import { ICONS_BASE as IMG_URL_ITEMS } from '../lib/assetUrls.js';

export default {
  mixins: [modalA11y],
  props: {
    slot: { type: String, required: true },            // friendly label e.g. "Weapon"
    groups: { type: Array, default: () => [] },        // [{ label, items: [names] }]; '' label = no header
    value: { type: String, default: '' },              // current item
    showAll: { type: Boolean, default: false },
    unlockable: { type: Boolean, default: false },     // show the "unlock all" toggle
    itemschanged: { type: Object, default: () => ({}) },
    // Per-item tile lines; default to combat stats / info from itemschanged. The Gear
    // Lab passes its own (roll counts / win-rate) so the picker stays data-agnostic.
    tileMeta: { type: Function, default: null },
    tileInfo: { type: Function, default: null },
  },
  emits: ['close', 'pick', 'toggle-showall'],
  data() { return { query: '' }; },
  mounted() { this.$nextTick(() => { try { this.$refs.search.focus(); } catch (e) { /* ignore */ } }); },
  computed: {
    filteredGroups() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.groups;
      return this.groups
        .map((g) => ({ label: g.label, items: g.items.filter((it) => it.toLowerCase().includes(q)) }))
        .filter((g) => g.items.length);
    },
  },
  methods: {
    iconUrl(name) { return IMG_URL_ITEMS + name.replace(/\s+/g, '') + '.gif'; },
    metaFor(name) { return this.tileMeta ? this.tileMeta(name) : itemStatSummary(this.itemschanged[name]); },
    infoFor(name) { if (this.tileInfo) return this.tileInfo(name); const it = this.itemschanged[name]; return (it && it.info) || name; },
    pick(name) { this.$emit('pick', name); this.$emit('close'); },
  },
};
</script>

<style scoped>
.gp-overlay { background: rgba(0, 0, 0, 0.66); padding: 28px 12px; }
.gp-modal { position: relative; background: var(--navy2); border: 1px solid var(--brass-edge); border-radius: 8px; width: min(760px, 96vw); max-height: 88vh; display: flex; flex-direction: column; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.55); color: var(--ink-hi); text-align: left; }
.gp-head { display: flex; align-items: center; gap: 14px; padding: 12px 16px 8px; border-bottom: 1px solid var(--line); }
.gp-head h3 { margin: 0; font-size: 16px; flex: 1; }
.gp-showall { font-size: 11px; color: var(--ink); cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
.gp-showall input { accent-color: var(--brass); margin: 0; }
.gp-close { background: none; border: none; color: var(--ink); font-size: 18px; cursor: pointer; }
.gp-search { margin: 10px 16px 4px; background: var(--navy2); color: #fff; border: 1px solid var(--brass-edge); border-radius: 6px; padding: 8px 10px; font-size: 13px; }
.gp-search:focus { outline: none; border-color: var(--brass-hi); }
.gp-body { overflow-y: auto; padding: 6px 16px 16px; }
.gp-group { margin-top: 10px; }
.gp-grouplabel { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--brass-hi); font-weight: 700; margin: 6px 0 5px; }
.gp-tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 6px; }
.gp-tile { display: flex; align-items: center; gap: 8px; text-align: left; background: var(--navy); color: #dce8f2; border: 1px solid #2c557b; border-radius: 6px; padding: 6px 8px; cursor: pointer; min-height: 40px; }
.gp-tile:hover { border-color: var(--brass-hi); background: #19324a; }
.gp-tile.on { background: rgba(217, 180, 94, 0.22); border-color: #9bd6ff; }
.gp-icon { width: 24px; height: 24px; object-fit: contain; image-rendering: pixelated; flex: 0 0 24px; }
.gp-meta { display: flex; flex-direction: column; min-width: 0; }
.gp-name { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gp-stats { font-size: 10px; opacity: 0.75; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gp-empty { min-height: 40px; }
.gp-empty .gp-x { color: #ff8a5f; font-weight: 700; width: 24px; text-align: center; }
.gp-none { opacity: 0.6; font-size: 13px; padding: 16px 0; }
</style>
